// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import RedirectToSignIn from "@bg/App/Components/RedirectToSignIn/RedirectToSignIn";
import WidgetTitle, { WidgetTitleStyles, widgetTitleStyles } from "@bg/App/Components/WidgetTitle";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import produce from "immer";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productsInfo: state.components.productInfoLists.productInfoListById[ownProps.id]?.productInfoByProductId,
    products: getProductsForProductInfoList(state, ownProps.id),
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    currencySymbol: getSession(state).currency?.currencySymbol,
    isAuthenticated: getSession(state).isAuthenticated,
});

const mapDispatchToProps = {
    loadCarouselProducts,
    loadCurrentCart,
    addCartLines: makeHandlerChainAwaitable(addCartLines as any),
};

type Props = OwnProps &
    HasProduct &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasHistory;

export interface ProductSuggestedProductsStyles {
    widgetTitle?: WidgetTitleStyles;
    widgetWrapper?: InjectableCss;
    topSection?: InjectableCss;
    imagesContainer?: InjectableCss;
    itemWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    atcSection?: InjectableCss;
    bottomSection?: InjectableCss;
    atcButton?: ButtonPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
    pdpLink?: ClickablePresentationProps;
}

export const productSuggestedProductsStyles: ProductSuggestedProductsStyles = {
    widgetTitle: {
        wrapper: {
            css: css`
                ${widgetTitleStyles.wrapper?.css}
            `,
        },
    },
    widgetWrapper: {
        css: css`
            margin-bottom: 70px;
        `,
    },
    topSection: {
        css: css`
            display: flex;
            @media (max-width: 1300px) {
                flex-wrap: wrap;
                margin-bottom: 15px;
            }
            @media print {
                display: none;
            }
        `,
    },
    imagesContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: calc(100% - 315px);
            @media (max-width: 1300px) {
                width: 100%;
            }
        `,
    },
    itemWrapper: {
        css: css`
            width: calc(100% / 8);
            display: flex;
            align-items: center;
            justify-content: space-around;
            margin-bottom: 25px;
            @media (max-width: 950px) {
                width: calc(100% / 6);
            }
            @media (max-width: 700px) {
                width: calc(100% / 3);
            }
            @media (max-width: 390px) {
                width: calc(100% / 2);
            }
        `,
    },

    atcSection: {
        css: css`
            margin-left: 20px;
            display: flex;
            flex-direction: column;
            @media (max-width: 1300px) {
                margin-left: 0;
            }
        `,
    },
    bottomSection: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    atcButton: {
        css: css`
            &&& {
                max-width: 160px;
                padding: 0 26px;
                background-color: ${getColor("primary")} !important;
                color: ${getColor("common.white")};
                &:hover:not(:disabled) {
                    background-color: ${getColor("common.white")} !important;
                    border-color: ${getColor("primary")};
                    color: ${getColor("primary")};
                }
                @media (max-width: 481px) {
                    max-width: 100%;
                }
            }
            &.loading {
                &,
                &:disabled {
                    &,
                    &:hover {
                        background: transparent !important;
                        border-color: transparent !important;
                        opacity: 1;
                    }
                }
            }
        `,
    },
    loadingSpinner: {
        size: 24,
    },
    pdpLink: {
        css: css`
            margin-left: 5px;
            @media print {
                display: flex !important;
            }
            @media (max-width: 481px) {
                display: flex;
                flex-wrap: wrap;
            }
        `,
    },
};

const styles = productSuggestedProductsStyles;

const SuggestedProductsNotification: React.FC<Props> = ({
    product,
    id,
    fields: { title },
    loadCarouselProducts,
    products,
    showAddToCartConfirmationDialog,
    currencySymbol,
    loadCurrentCart,
    addCartLines,
    isAuthenticated,
    history,
    productsInfo,
}) => {
    const [isValid, setIsValid] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [productsData, setProductData] = useState<ProductSuggestedDto[]>([]);
    const [noneSelected, setNoneSelected] = useState(false);
    const [hasRequireQuote, setHasRequireQuote] = useState(false);

    useEffect(() => {
        if (product) {
            loadCarouselProducts({
                carouselId: id,
                carouselType: "suggestedProducts",
                seedWithManuallyAssigned: "",
                relatedProductType: "suggestedProducts",
                numberOfProductsToDisplay: 16,
                isProductDetailsPage: true,
                productId: product?.id,
                displayProductsFrom: "",
                selectedCategoryIds: [],
                isProductListPage: false,
                isBrandDetailsPage: false,
            });
        }
    }, [product?.id]);

    const toasterContext = useContext(ToasterContext);

    const [isPriced, setIsPriced] = useState(false);
    useEffect(() => {
        if (products && productsInfo) {
            const productData = createFromProductContext(products, productsInfo);
            setHasRequireQuote(productData.some(o => o.quoteRequired));
            setProductData(productData);
            Object.keys(productsInfo).forEach(info => {
                if (!productsInfo[info]?.pricing) {
                    setIsPriced(false);
                } else {
                    setIsPriced(true);
                }
            });
        }
    }, [products, productsInfo]);

    useEffect(() => {
        if (productsData) {
            calculateTotal(productsData);
        }
    }, [productsData]);

    // calcuate total and validation as well
    const calculateTotal = useCallback(
        (data: ProductSuggestedDto[]) => {
            if (data.length && productsInfo) {
                let total = 0;
                const validation = data.every((productInfo: ProductSuggestedDto) => {
                    total += productInfo.selected ? productInfo.pricing || 0 : 0;
                    return productInfo.qtyOrdered ? productInfo.qtyOrdered > 0 : true;
                });
                setTotalPrice(total);
                setIsValid(validation);
                setNoneSelected(data.filter(o => !o.selected).length === data.length);
            }
        },
        [productsData],
    );

    const addAllToCartClickHandler = async () => {
        if (productsInfo) {
            setIsLoading(true);
            const filteredInfos: ProductInfo[] = [];
            productsData?.filter((o, i) => {
                if (productsInfo[o.productId] && o.selected) {
                    filteredInfos.push(productsInfo[o.productId]!);
                }
            });

            const cartLinesCollection = (await addCartLines({
                productInfos: filteredInfos,
            })) as unknown as CartLineCollectionModel;

            if (showAddToCartConfirmationDialog) {
                const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                    messageType: "success",
                });
            }

            loadCurrentCart({ shouldLoadFullCart: true, onSuccess: result => setIsLoading(false) });
        }
    };

    const toggleProductSelection = (_: React.SyntheticEvent<Element, Event>, product: ProductSuggestedDto) => {
        const newSelectedProducts = produce(productsData, draft => {
            draft.filter(p => p.productId === product.productId)[0].selected = !product.selected;
        });
        setProductData(newSelectedProducts);
        calculateTotal(newSelectedProducts);
    };

    const filteredProducts = productsData.filter(o => o.selected);

    if (!products || !products.length || !productsInfo) {
        return null;
    }

    return (
        <StyledWrapper {...styles.widgetWrapper} className={`carousel-data_${id}`}>
            {title && <WidgetTitle title={title} extendedStyles={styles.widgetTitle} />}
            {!noneSelected && (
                <StyledWrapper {...styles.topSection}>
                    <StyledWrapper {...styles.imagesContainer}>
                        {/* eslint-disable-next-line array-callback-return */}
                        {filteredProducts.map((product, index) => (
                            <StyledWrapper
                                key={product.productId}
                                {...styles.itemWrapper}
                                className={index === 0 || index === products!.length / 2 ? "newRow" : ""}
                            >
                                <LazyImage
                                    src={product.productImage}
                                    altText={product.imageAltText || product.productTitle}
                                    cloudinaryWidth="100"
                                    cloudinary={true}
                                    {...styles.productImage}
                                />
                                <Icon
                                    src="Plus"
                                    size={15}
                                    color="secondary"
                                    css={css`
                                        visibility: ${index + 1 !== filteredProducts.length ? "visible" : "hidden"};
                                        svg polygon {
                                            fill: ${getColor("secondary")};
                                        }
                                    `}
                                />
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                    <StyledWrapper {...styles.atcSection}>
                        {isPriced && !hasRequireQuote && <Typography>{translate("Price for Additions:")}</Typography>}
                        <Typography variant="h5" forwardAs="span">
                            {isPriced && !hasRequireQuote ? (
                                `${totalPrice ? `${currencySymbol} ${totalPrice.toFixed(2)}` : "N/A"}`
                            ) : (
                                <>
                                    {hasRequireQuote ? (
                                        <Typography>{translate("Quote Required")}</Typography>
                                    ) : (
                                        <RedirectToSignIn>
                                            <Typography
                                                color="primary"
                                                size={fontConfig.fontSize.xSmall}
                                                weight={fontConfig.weight.semiBold}
                                            >
                                                {siteMessage("Pricing_SignInForPrice")}
                                            </Typography>
                                        </RedirectToSignIn>
                                    )}
                                </>
                            )}
                        </Typography>

                        <Button
                            className={isLoading ? "loading" : ""}
                            onClick={addAllToCartClickHandler}
                            disabled={!isValid || !productsInfo || isLoading || !isPriced || !productsInfo}
                            {...styles.atcButton}
                        >
                            {isLoading ? <LoadingSpinner {...styles.loadingSpinner} /> : translate("Add To Cart")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.bottomSection}>
                <Typography
                    css={css`
                        display: none;
                        @media print {
                            display: block;
                        }
                    `}
                    variant="h5"
                    weight={fontConfig.weight.bold}
                >
                    {title}
                </Typography>
                {productsData.map(o => (
                    <StyledWrapper
                        key={`${o.productId}_checkbox`}
                        css={css`
                            margin-bottom: 15px;
                            display: flex;
                            align-items: center;
                            @media print {
                                div[data-id="checkbox"] {
                                    display: none;
                                }
                            }
                        `}
                    >
                        <Checkbox checked={o.selected} onChange={e => toggleProductSelection(e, o)}>
                            {translate("")}
                        </Checkbox>
                        {o.url ? (
                            <Clickable
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    history.push(o.url!);
                                }}
                                {...styles.pdpLink}
                            >
                                <Typography>{parse(o.productTitle)}</Typography>&nbsp;&nbsp;&nbsp;
                                {o.quoteRequired ? (
                                    <Typography weight={fontConfig.weight.semiBold}>
                                        {translate("Requires Quote")}
                                    </Typography>
                                ) : (
                                    <Typography weight={fontConfig.weight.semiBold}>
                                        {o.netPricingDisplay} {o.netPricingDisplay && o.uom ? `/ ${o.uom}` : ""}
                                    </Typography>
                                )}
                            </Clickable>
                        ) : (
                            <StyledWrapper
                                css={css`
                                    margin-left: 5px;
                                `}
                            >
                                <Typography>{parse(o.productTitle)}</Typography>&nbsp;&nbsp;&nbsp;
                                {o.quoteRequired ? (
                                    <Typography weight={fontConfig.weight.semiBold}>
                                        {translate("Requires Quote")}
                                    </Typography>
                                ) : (
                                    <Typography weight={fontConfig.weight.semiBold}>
                                        {o.netPricingDisplay} {o.netPricingDisplay && o.uom ? `/ ${o.uom}` : ""}
                                    </Typography>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

interface ProductSuggestedDto {
    productTitle: string;
    productImage: string;
    imageAltText?: string;
    productId: string;
    pricing?: number;
    netPricingDisplay?: string;
    uom?: string;
    qtyOrdered?: number;
    selected: boolean;
    url?: string;
    quoteRequired?: boolean;
}

const createFromProductContext = (products?: ProductModel[], productInfos?: any) => {
    const productData: ProductSuggestedDto[] = [];
    if (products && productInfos) {
        // eslint-disable-next-line array-callback-return
        products.map(o => {
            const info = productInfos[o.id];
            const pricing = info?.pricing?.unitNetPrice;
            const netPricingDisplay = info?.pricing?.unitNetPriceDisplay;
            const uom = info?.unitOfMeasure;
            const qtyOrdered = info?.qtyOrdered;
            const url = info?.productDetailPath;
            productData.push({
                productTitle: o.productTitle,
                productImage: o.smallImagePath,
                imageAltText: o.imageAltText,
                productId: o.id,
                pricing,
                netPricingDisplay,
                uom,
                qtyOrdered,
                selected: true,
                url,
                quoteRequired: o.quoteRequired,
            });
        });
    }
    return productData;
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(withHistory(SuggestedProductsNotification))),
    definition: {
        displayName: "Suggested Products",
        group: "XNGAGE" as any,
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
